/**
 * Photos of the home destination tiles («Partout où vous atterrissez»), by city. `cars-by-city` only returns city
 * names, so the photo is chosen here. Unsplash photos (free licence, see `assets/images/destinations/CREDITS.md`),
 * self-hosted as WebP in two widths: `<key>-640.webp` and `<key>-1280.webp`. A small city without a photo of its own
 * gets one of its region (Khenifra: Middle Atlas, Laayoune: Sahara); an unknown city keeps the tinted tile.
 */
const files = import.meta.glob<string>('@/assets/images/destinations/*.webp', { eager: true, import: 'default' });

interface DestinationPhoto {
  image: string;
  imageSrcSet: string;
}

const PHOTOS = new Map<string, DestinationPhoto>();
for (const [path, url] of Object.entries(files)) {
  const m = /\/([a-z-]+)-640\.webp$/.exec(path);
  const large = m && files[path.replace('-640.webp', '-1280.webp')];
  if (m?.[1] && large) PHOTOS.set(m[1], { image: url, imageSrcSet: `${url} 640w, ${large} 1280w` });
}

/** City names spelled another way by the backend. */
const ALIASES: Record<string, string> = { tanger: 'tangier' };

export const cityKey = (city: string) => city.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().trim();

/**
 * «Fès» → the Fès photo; «Casablanca - Aéroport Mohammed V» → that airport's photo (`airport-casablanca`), else the
 * generic terminal (`airport-marrakech`); an unknown city → none.
 */
export function destinationPhoto(city: string): DestinationPhoto | undefined {
  const key = cityKey(city);
  const base = key.split(' - ')[0]?.trim() ?? key;
  const name = ALIASES[base] ?? base;
  if (/\baeroport\b|\bairport\b/.test(key)) return PHOTOS.get(`airport-${name}`) ?? PHOTOS.get('airport-marrakech');
  return PHOTOS.get(name);
}
